import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';

const initialState = {
    isLoading: false,
    isLoadingMore: false,
    error: null,
    twilioPhones: [],
    selectedPhoneId: null,
    callLogs: [],
    callLogsCount: 0,
    hasMore: true,
    currentOffset: 0,
    limit: 25,
    filters: {
        startDate: null,
        endDate: null,
        leadId: null,
        status: null, // 'completed', 'busy', 'no-answer', etc.
        callerSearch: '', // caller__icontains
        leadIds: [], // lead filter by selected lead ids
        leadIsNull: null, // true/false toggle for lead__isnull
    },
    transcriptions: {}, // { [callId]: { isLoading, data, error } }
};

const slice = createSlice({
    name: 'practiceCallLogs',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setLoadingMore(state, action) {
            state.isLoadingMore = action.payload;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.isLoadingMore = false;
            state.error = action.payload;
        },
        setTwilioPhones(state, action) {
            state.twilioPhones = action.payload;
            // Auto-select first phone if available and none selected
            if (action.payload.length > 0 && !state.selectedPhoneId) {
                state.selectedPhoneId = action.payload[0].id;
            }
        },
        setSelectedPhone(state, action) {
            state.selectedPhoneId = action.payload;
            // Reset call logs when switching phones
            state.callLogs = [];
            state.callLogsCount = 0;
            state.currentOffset = 0;
            state.hasMore = true;
        },
        setCallLogs(state, action) {
            state.callLogs = action.payload.results || [];
            state.callLogsCount = action.payload.count || 0;
            state.currentOffset = state.limit;
            state.hasMore = (action.payload.results?.length || 0) < (action.payload.count || 0);
            state.isLoading = false;
        },
        appendCallLogs(state, action) {
            const newLogs = action.payload.results || [];
            state.callLogs = [...state.callLogs, ...newLogs];
            state.callLogsCount = action.payload.count || 0;
            state.currentOffset += newLogs.length;
            state.hasMore = state.callLogs.length < (action.payload.count || 0);
            state.isLoadingMore = false;
        },
        setFilters(state, action) {
            state.filters = { ...state.filters, ...action.payload };
            // Reset pagination when filters change
            state.callLogs = [];
            state.currentOffset = 0;
            state.hasMore = true;
        },
        clearFilters(state) {
            state.filters = {
                startDate: null, endDate: null, leadId: state.filters.leadId,
                status: null, callerSearch: '', leadIds: [], leadIsNull: null,
            };
            state.callLogs = [];
            state.currentOffset = 0;
            state.hasMore = true;
        },
        updateCallLogLead(state, action) {
            const { callId, lead } = action.payload;
            const idx = state.callLogs.findIndex((l) => l.id === callId);
            if (idx !== -1) {
                state.callLogs[idx] = { ...state.callLogs[idx], lead, lead_name: action.payload.leadName || null };
            }
        },
        setLeadFilter(state, action) {
            state.filters.leadId = action.payload;
        },
        // Transcription states
        setTranscriptionLoading(state, action) {
            const { callId, isLoading } = action.payload;
            if (!state.transcriptions[callId]) {
                state.transcriptions[callId] = { isLoading: false, data: null, error: null };
            }
            state.transcriptions[callId].isLoading = isLoading;
        },
        setTranscriptionData(state, action) {
            const { callId, data } = action.payload;
            if (!state.transcriptions[callId]) {
                state.transcriptions[callId] = { isLoading: false, data: null, error: null };
            }
            state.transcriptions[callId].data = data;
            state.transcriptions[callId].isLoading = false;
            state.transcriptions[callId].error = null;
        },
        setTranscriptionError(state, action) {
            const { callId, error } = action.payload;
            if (!state.transcriptions[callId]) {
                state.transcriptions[callId] = { isLoading: false, data: null, error: null };
            }
            state.transcriptions[callId].error = error;
            state.transcriptions[callId].isLoading = false;
        },
    }
});

// Reducer
export default slice.reducer;

export const { reset, setLoading, setCallLogs, setSelectedPhone, setFilters, clearFilters, setLeadFilter, updateCallLogLead } = slice.actions;

// Build query string with filters
const buildQueryString = (limit, offset, filters) => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', offset);
    params.append('ordering', '-created_at');

    if (filters?.startDate) {
        params.append('created_at__gte', filters.startDate);
    }
    if (filters?.endDate) {
        params.append('created_at__lte', filters.endDate);
    }
    if (filters?.leadId) {
        params.append('lead', filters.leadId);
    }
    if (filters?.status) {
        params.append('status', filters.status);
    }
    if (filters?.callerSearch) {
        params.append('caller__icontains', filters.callerSearch);
    }
    if (filters?.leadIds && filters.leadIds.length > 0) {
        filters.leadIds.forEach((id) => {
            params.append('lead', id);
        });
    }
    if (filters?.leadIsNull === true || filters?.leadIsNull === false) {
        params.append('lead__isnull', filters.leadIsNull);
    }

    return params.toString();
};

// Get practice Twilio phones
export function getPracticeTwilioPhones(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/`);
            if (response.status === 200) {
                const phones = Array.isArray(response.data) ? response.data : response.data?.results || [];
                dispatch(slice.actions.setTwilioPhones(phones));
                return phones;
            }
            dispatch(slice.actions.setLoading(false));
            return [];
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return [];
        }
    };
}

// Get call logs for a specific Twilio phone with filters
export function getCallLogsForPhone(practiceId, phoneId, filters = {}, isLoadMore = false) {
    return async (dispatchFn, getState) => {
        try {
            const state = getState().practiceCallLogs;
            const offset = isLoadMore ? state.currentOffset : 0;
            const { limit } = state;

            if (isLoadMore) {
                dispatch(slice.actions.setLoadingMore(true));
            } else {
                dispatch(slice.actions.setLoading(true));
            }

            const queryString = buildQueryString(limit, offset, filters);
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/?${queryString}`);

            if (response.status === 200) {
                if (isLoadMore) {
                    dispatch(slice.actions.appendCallLogs(response.data));
                } else {
                    dispatch(slice.actions.setCallLogs(response.data));
                }
                return response.data;
            }

            if (isLoadMore) {
                dispatch(slice.actions.setLoadingMore(false));
            } else {
                dispatch(slice.actions.setLoading(false));
            }
            return { results: [], count: 0 };
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return { results: [], count: 0 };
        }
    };
}

// Initialize - fetch phones first, then fetch logs for first phone
export function initializePracticeCallLogs(practiceId, leadId = null) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));

            // Set lead filter if provided
            if (leadId) {
                dispatch(slice.actions.setLeadFilter(leadId));
            }

            // First get all Twilio phones
            const phonesResponse = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/`);
            if (phonesResponse.status === 200) {
                const phones = Array.isArray(phonesResponse.data) ? phonesResponse.data : phonesResponse.data?.results || [];
                dispatch(slice.actions.setTwilioPhones(phones));

                // Fetch call logs for the first phone if available
                if (phones.length > 0) {
                    const firstPhoneId = phones[0].id;
                    const filters = leadId ? { leadId } : {};
                    const queryString = buildQueryString(25, 0, filters);
                    const callsResponse = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${firstPhoneId}/calls/?${queryString}`);

                    if (callsResponse.status === 200) {
                        dispatch(slice.actions.setCallLogs(callsResponse.data));
                    } else {
                        dispatch(slice.actions.setLoading(false));
                    }
                } else {
                    dispatch(slice.actions.setLoading(false));
                }
            } else {
                dispatch(slice.actions.setLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Change selected phone and fetch its logs
export function selectPhoneAndFetchLogs(practiceId, phoneId, filters = {}) {
    return async () => {
        try {
            dispatch(slice.actions.setSelectedPhone(phoneId));
            dispatch(slice.actions.setLoading(true));

            const queryString = buildQueryString(25, 0, filters);
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/?${queryString}`);

            if (response.status === 200) {
                dispatch(slice.actions.setCallLogs(response.data));
            } else {
                dispatch(slice.actions.setLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Apply filters and refetch
export function applyFiltersAndFetch(practiceId, phoneId, filters) {
    return async (dispatchFn, getState) => {
        try {
            const state = getState().practiceCallLogs;
            // Merge with existing leadId filter
            const mergedFilters = { ...filters, leadId: state.filters.leadId };

            dispatch(slice.actions.setFilters(mergedFilters));
            dispatch(slice.actions.setLoading(true));

            const queryString = buildQueryString(25, 0, mergedFilters);
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/?${queryString}`);

            if (response.status === 200) {
                dispatch(slice.actions.setCallLogs(response.data));
            } else {
                dispatch(slice.actions.setLoading(false));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Load more (infinite scroll)
export function loadMoreCallLogs(practiceId, phoneId) {
    return async (dispatchFn, getState) => {
        const state = getState().practiceCallLogs;
        if (state.isLoadingMore || !state.hasMore) return;

        try {
            dispatch(slice.actions.setLoadingMore(true));

            const queryString = buildQueryString(state.limit, state.currentOffset, state.filters);
            const response = await callApi(`/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/?${queryString}`);

            if (response.status === 200) {
                dispatch(slice.actions.appendCallLogs(response.data));
            } else {
                dispatch(slice.actions.setLoadingMore(false));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Patch a call to assign/update lead
export function patchCallLead(practiceId, phoneId, callId, leadId, leadName) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/${callId}/`,
                'patch',
                { lead: leadId },
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.updateCallLogLead({ callId, lead: leadId, leadName }));
                return response.data;
            }
            return null;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return null;
        }
    };
}

// Transcribe a call recording
export function transcribeCall(practiceId, phoneId, callId) {
    return async () => {
        try {
            dispatch(slice.actions.setTranscriptionLoading({ callId, isLoading: true }));

            // Use longer timeout for transcription (60 seconds) as it processes audio
            const response = await callApi(
                `/api/v1/practices/${practiceId}/practice-twilio-phones/${phoneId}/calls/${callId}/transcribe/`,
                'post',
                null,
                false,
                { timeout: 60000 }
            );

            if (response.status === 200 || response.status === 201) {
                // Handle various response formats: transcript, transcription, text, or raw data
                const transcriptText = response.data?.transcript || response.data?.transcription || response.data?.text ||
                    (typeof response.data === 'string' ? response.data : null);
                dispatch(slice.actions.setTranscriptionData({
                    callId,
                    data: transcriptText
                }));
                return response.data;
            }

            dispatch(slice.actions.setTranscriptionError({
                callId,
                error: 'Failed to transcribe call'
            }));
            return null;
        } catch (error) {
            dispatch(slice.actions.setTranscriptionError({
                callId,
                error: error?.message || 'Failed to transcribe call'
            }));
            return null;
        }
    };
}
